/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Button, Heading, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync, isUserTypeB2B } from '@msdyn365-commerce/core';
import { SalesOrder, SimpleProduct, OrderOriginator, ProductCatalog } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classname from 'classnames';
import get from 'lodash/get';
import { computed, observable } from 'mobx';
import * as React from 'react';
import { IGetOrderSummaryInput, IHelp, IOrderSummary, IPaymentMethods, IPaymentMethodsInput } from './common';
import { getGroups, IGroups } from './common/get-groups';
import { getOrderInfomationInput, getOrderInformation, IOrderInformation } from './common/get-order-information';
import { ISalesLine } from './common/get-sales-line';
import { getSalesLines, getSalesLinesInput } from './common/get-sales-lines';
import LabelComponent from './common/label';
import { getTenderIdOperationIdMap, IPriceContext } from './common/utilities';
import { IMandsOrderDetailsData } from './mands-order-details.data';
import { IMandsOrderDetailsProps } from './mands-order-details.props.autogenerated';
import { getOrderDetailsHelp } from './components/get-help';
import { getOrderDetailsOrderSummary } from './components/get-order-summary';
import { getOrderDetailsPaymentMethods } from './components/get-payment-methods';

export * from './common/get-groups';
export * from './common/get-order-information';
export * from './common/get-sales-line';
export * from './common/get-sales-lines';
export * from './common/utilities';
export * from './components/get-help';
export * from './components/get-order-summary';
export * from './components/get-payment-methods';

export interface IOrderDetailsViewProps extends IMandsOrderDetailsProps<IMandsOrderDetailsData> {
    moduleProps: IModuleProps;
    salesOrder?: SalesOrder;
    footerProps?: INodeProps;
    alert?: React.ReactNode;
    loading?: React.ReactNode;
    heading: React.ReactNode;
    placedBy?: React.ReactNode;
    backToShoppingLink?: React.ReactNode;
    orderInfomation?: IOrderInformation;
    salesLines?: ISalesLine[];
    groups?: IGroups;
    orderSummary?: IOrderSummary;
    payment?: IPaymentMethods;
    help?: IHelp;
    catalogs?: ProductCatalog[];
}

/**
 *
 * OrderDetails component
 * @extends {React.PureComponent<IMandsOrderDetailsProps<IMandsOrderDetailsData>>}
 */
class OrderDetails extends React.PureComponent<IMandsOrderDetailsProps<IMandsOrderDetailsData>> {
    private moduleName: string = 'ms-order-details';
    @observable private _isSelectionModeEnabled: boolean;
    @computed get isCurrentChannel(): boolean {
        if (!this.order) {
            return false;
        }
        return this.order.ChannelId === get(this.props, 'context.request.apiSettings.channelId');
    }

    @computed get isDataReady(): boolean {
        return get(this.props, 'data.orderHydration.status') === 'SUCCESS';
    }

    @computed get hasError(): boolean {
        return get(this.props, 'data.orderHydration.status') === 'FAILED';
    }

    @computed get order(): SalesOrder | undefined {
        return get(this.props, 'data.orderHydration.result.salesOrder');
    }

    @computed get products(): SimpleProduct[] {
        return get(this.props, 'data.orderHydration.result.products') || [];
    }

    @computed get priceContext(): IPriceContext {
        const {
            id,
            typeName,
            context,
            telemetry,
            resources: { freePriceText }
        } = this.props;
        return {
            id,
            typeName,
            context,
            telemetry,
            freePriceText
        };
    }

    @computed get earnedPoints(): number {
        const loyaltyCard = this.props.data.loyaltyCard.result;
        if (loyaltyCard && loyaltyCard.RewardPoints && this.order) {
            const pointPrograms = loyaltyCard.RewardPoints.map(point => point.RewardPointId);
            const rewardLines = this.order.LoyaltyRewardPointLines || [];
            let total = 0;
            rewardLines.forEach(line => {
                if ((line.RewardPointAmountQuantity || 0) > 0 && pointPrograms.indexOf(line.RewardPointId) > -1) {
                    total += line.RewardPointAmountQuantity!;
                }
            });
            return total;
        }
        return 0;
    }

    @computed get orderSummaryProps(): IGetOrderSummaryInput | undefined {
        if (!this.order) {
            return;
        }

        const {
            resources: {
                orderSummaryHeading,
                orderSummaryItemsTotalLabel,
                orderSummaryShippingFeeLabel,
                orderSummaryTaxLabel,
                orderSummaryGrandTotalLabel,
                pointsEarnedLabel
            }
        } = this.props;
        return {
            order: this.order,
            earnedPoints: this.earnedPoints,
            priceContext: this.priceContext,
            resource: {
                orderSummaryHeading,
                orderSummaryItemsTotalLabel,
                orderSummaryShippingFeeLabel,
                orderSummaryTaxLabel,
                pointsEarnedLabel,
                orderSummaryGrandTotalLabel
            }
        };
    }

    @computed get paymentMethodsProps(): IPaymentMethodsInput | undefined {
        const tenderTypes = this.props.data.tenderTypes.result;
        if (!this.order || !this.order.TenderLines || !tenderTypes) {
            return;
        }

        const locale = get(this.props, 'context.request.locale') || 'en-US';

        const {
            resources: {
                paymentMethodsTitle,
                creditCardEndingLabel,
                giftCardEndingLabel,
                amountCoveredLabel,
                cashUsedLabel,
                loyaltyCardUsedLabel
            }
        } = this.props;

        return {
            priceContext: this.priceContext,
            tenderLines: this.order.TenderLines,
            tenderIdOperationIdMap: getTenderIdOperationIdMap(tenderTypes),
            locale,
            resource: {
                paymentMethodsTitle,
                creditCardEndingLabel,
                giftCardEndingLabel,
                amountCoveredLabel,
                cashUsedLabel,
                loyaltyCardUsedLabel
            },
            priceCurrency: this.order.CurrencyCode
        };
    }

    public constructor(props: IMandsOrderDetailsProps<IMandsOrderDetailsData>) {
        super(props);
        this._isSelectionModeEnabled = false;
    }
    public render(): JSX.Element {
        const {
            resources: {
                noSalesOrderText,
                genericErrorMessage,
                needHelpLabel,
                helpLineNumberLabel,
                loadingLabel,
                backToShopping,
                orderItemLabel,
                orderItemsLabel,
                helpLineContactAriaLabel
            },
            config: { heading, contactNumber, shouldShowQrCode, showChannelInfo }
        } = this.props;
        const customer = this.props.data.customerInformation && this.props.data.customerInformation.result;
        const originators = this.props.data.orderOriginators && this.props.data.orderOriginators.result;
        const originator: OrderOriginator | undefined = originators && originators.length === 1 ? originators[0] : undefined;
        const moduleProps = { moduleProps: this.props, className: classname('ms-order-details', this.props.config.className) };
        const backToShoppingUrl = getUrlSync('home', this.props.context.actionContext) || '';
        const channelIdentities = this.props.data.channels.result?.channelIdentities;
        const orgUnitLocations = this.props.data.orgUnitLocations.result?.orgUnitLocations;

        const orderInformationInput =
            this.order &&
            getOrderInfomationInput(
                this.props,
                this.moduleName,
                this.order,
                this.priceContext,
                channelIdentities,
                orgUnitLocations,
                customer,
                originator,
                showChannelInfo,
                false,
                shouldShowQrCode ?? true
            );
        const orderInformation =
            this._isSelectionModeEnabled || !orderInformationInput ? undefined : getOrderInformation(orderInformationInput);

        let viewProps: IOrderDetailsViewProps = {
            ...this.props,
            moduleProps,
            heading: heading && <Heading {...heading} className='ms-order-details__heading' />,
            backToShoppingLink: backToShoppingUrl && (
                <Button className='ms-order-details__btn-keep-shopping' href={backToShoppingUrl}>
                    {backToShopping}
                </Button>
            ),
            footerProps: { className: 'ms-order-details__footer' }
        };
        if (isUserTypeB2B(this.props.context.request) && orderInformationInput?.isOnlineStore && originator) {
            viewProps = {
                ...viewProps,
                placedBy: orderInformation?.placedBy
            };
        }

        if (this.hasError) {
            viewProps = {
                ...viewProps,
                alert: <LabelComponent className='ms-order-details__alert' label={genericErrorMessage} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!this.isDataReady) {
            viewProps = {
                ...viewProps,
                loading: <LabelComponent className='ms-order-details__loading' label={loadingLabel} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!this.order) {
            viewProps = {
                ...viewProps,
                alert: <LabelComponent className='ms-order-details__alert' label={noSalesOrderText} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        const salesLinesInput = getSalesLinesInput(this.props, this.moduleName, this.order, this.products, this.priceContext);
        const salesLines = salesLinesInput && getSalesLines(salesLinesInput);

        const orderInfomationInput = this.order && getOrderInfomationInput(this.props, this.moduleName, this.order, this.priceContext);
        const orderInfomation = orderInfomationInput && getOrderInformation(orderInfomationInput);

        viewProps = {
            ...viewProps,
            salesOrder: this.order,
            orderInfomation,
            salesLines,
            groups: salesLines
                ? getGroups({
                      moduleName: this.moduleName,
                      salesLines,
                      resources: { orderItemLabel, orderItemsLabel }
                  })
                : undefined,
            orderSummary: this.orderSummaryProps ? getOrderDetailsOrderSummary(this.orderSummaryProps) : undefined,
            payment: this.paymentMethodsProps ? getOrderDetailsPaymentMethods(this.paymentMethodsProps) : undefined,
            help: contactNumber
                ? getOrderDetailsHelp({ needHelpLabel, helpLineNumberLabel, contactNumber, helpLineContactAriaLabel })
                : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }
}

export default OrderDetails;
